import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link, Route, Switch } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import {
  getConnectPoints,
  deleteConnectPoint,
  getCollectionWidgetLinkedWebsites,
  duplicateWidget,
  saveConnectPoint,
} from 'services/connect-card.service';
import toast from 'utils/toast';
import useAlert from 'hooks/useAlert';
import { useSelector } from 'react-redux';
import Button from 'components/Button';
import DataTable from 'components/DataTable';
import Typography from '@mui/material/Typography';
import Dropdown from 'components/Dropdown';
import IconButton from 'components/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ViewIcon from '@mui/icons-material/VisibilityOutlined';
import orderBy from 'lodash/orderBy';
import escapeRegExp from 'lodash/escapeRegExp';
import Access, { MODULES, ACTIONS } from 'components/Access';
import useRequest from 'hooks/useRequest';
import TextInput from '../../../components/form/TextInput';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolderOutlined';

const filter = (cards, query) => {
  const regx = new RegExp(escapeRegExp(query), 'i');
  return cards.filter((card) => {
    return (
      regx.test(card.collection_name) || regx.test(card.collection_description)
    );
  });
};

const ConnectWidgets = ({ tab }) => {
  const [t] = useTranslation();
  const alert = useAlert();
  const [links, setLinks] = useState();
  const account = useSelector((s) => s.settings.account);
  const [widgets, { loading, refetch, error, updateCache }] = useRequest(
    getConnectPoints,
    {
      collection_type: 'widget',
      account_id: account.account_id,
      load_cards: '1',
      collection_deleted: tab === 'active' ? 0 : 1,
    },
  );
  const [sort, setSort] = useState({
    sortField: 'card_name',
    sortOrder: 'asc',
  });
  const [query, setQuery] = useState('');

  useEffect(() => {
    getCollectionWidgetLinkedWebsites(account.hash).then(setLinks);
  }, []);

  const handleMenuClick = async (action, data) => {
    switch (action) {
      case 'duplicate':
        try {
          await duplicateWidget(data.hash);
          toast.success('Widget duplicated.');
          refetch();
        } catch (e) {
          toast.error(e.message);
        }
        break;
      case 'delete':
        alert.confirm({
          severity: 'warning',
          description: 'Are you sure you want to delete this widget?',
          onConfirm: async () => {
            try {
              await saveConnectPoint({
                hash: data.hash,
                collection_deleted: 1,
              });
              toast.success('Widget deleted.');
              return refetch();
            } catch (e) {
              toast.error(e.message);
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const onRestore = (row) => {
    return saveConnectPoint({
      hash: row.hash,
      collection_deleted: 0,
    })
      .then(() => {
        updateCache((cards) => cards.filter((card) => card.hash !== row.hash));
        toast.success('Widget restored.');
      })
      .catch((e) => {
        toast.error(e.message);
      });
  };

  const onTableChange = (type, props) => {
    setSort(props);
  };

  const columns = [
    {
      text: 'Name',
      dataField: 'collection_name',
      sortable: true,
      formatter: (cell, row) => {
        return (
          <>
            <Typography gutterBottom fontWeight={500}>
              {cell}
            </Typography>
            <Typography color="textSecondary">
              {row.collection_description}
            </Typography>
          </>
        );
      },
    },
    {
      dataField: 'collection_visits',
      align: 'center',
      sortable: true,
      text: 'Launches',
    },
    {
      dataField: 'cards',
      sortable: true,
      align: 'center',
      text: 'Active Cards',
      formatter: (val) => {
        return (
          <Stack
            justifyContent="center"
            alignItems="center"
            direction="row"
            spacing={0.5}
          >
            <Typography>{val.length}</Typography>
            {val.length ? (
              <Tooltip
                title={
                  <span style={{ whiteSpace: 'pre-line' }}>
                    {val.map((i) => i.card_name).join('\n')}
                  </span>
                }
              >
                <ViewIcon />
              </Tooltip>
            ) : null}
          </Stack>
        );
      },
    },
    {
      dataField: 'collection_settings',
      text: 'Enabled Widgets',
      formatter: (val) => {
        const widgets = [];
        if (val.chat?.enabled) {
          widgets.push('Chat Bubble');
        }
        if (val.stickyButton?.enabled) {
          widgets.push('Sticky Button');
        }
        if (val.popup?.enabled) {
          widgets.push('Timed Popup');
        }
        if (val.banner?.enabled) {
          widgets.push('Sticky Banner');
        }
        return (
          <Stack direction="row" spacing={0.5}>
            {widgets.map((widget, i) => (
              <Chip size="small" key={i} label={widget} />
            ))}
          </Stack>
        );
      },
    },
    {
      dataField: 'hash',
      align: 'right',
      formatter: (cell, row) => {
        if (row.collection_deleted === '1')
          return (
            <Access module={MODULES.CONNECT_CARDS} action={ACTIONS.MODIFY}>
              <Button variant="text" onClick={() => onRestore(row)}>
                Restore
              </Button>
            </Access>
          );
        const actions = [
          {
            key: 'edit',
            children: 'Edit',
            component: Link,
            to: `/connect/widgets/${cell}`,
          },
          {
            key: 'duplicate',
            children: 'Duplicate',
          },
          { key: 'delete', children: 'Delete' },
        ];
        return (
          <Access action={ACTIONS.MODIFY} module={MODULES.CONNECT_CARDS}>
            <Dropdown
              onSelect={(v) => handleMenuClick(v, row)}
              id="actions"
              options={actions}
            >
              <IconButton icon={<MoreHorizIcon />} />
            </Dropdown>
          </Access>
        );
      },
    },
  ];

  const tableData = useMemo(() => {
    const { sortField, sortOrder } = sort;
    if (!widgets) {
      return [];
    }
    const filtered = query ? filter(widgets, query) : widgets;

    return orderBy(filtered, sortField, sortOrder);
  }, [widgets, query, sort]);

  return (
    <Box>
      <Helmet title="Connect widgets" />
      <Stack
        borderBottom={1}
        borderColor="divider"
        px={2}
        py={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <TextInput
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          margin="none"
          size="small"
          startAdornment={<SearchIcon color="primary" />}
          sx={{ maxWidth: 300 }}
        />
        <Stack spacing={1} direction="row" alignItems="center">
          <Access action={ACTIONS.MODIFY} module={MODULES.CONNECT_CARDS}>
            <Button
              component={Link}
              to="/connect/widgets/create"
              startIcon={<AddCircleIcon />}
            >
              Add New
            </Button>
          </Access>
        </Stack>
      </Stack>
      {error && <Alert severity="error">{error.message}</Alert>}
      <>
        {!links?.linked_website?.length ? (
          <Alert
            sx={{ mx: 2, mt: 2, alignItems: 'center' }}
            severity="error"
            action={
              <Access action={ACTIONS.MODIFY} module={MODULES.CONNECT_CARDS}>
                <Button
                  component={Link}
                  to="/connect/website/setup"
                  color="secondary"
                >
                  Link Your Website
                </Button>
              </Access>
            }
          >
            <b>ACTION REQUIRED:</b> You must first link your website in order to
            add Connect Widgets to your church website.
          </Alert>
        ) : (
          <Alert
            sx={{ mx: 2, mt: 2, alignItems: 'center' }}
            severity="info"
            action={
              <Access action={ACTIONS.MODIFY} module={MODULES.CONNECT_CARDS}>
                <Button
                  component={Link}
                  to="/connect/website/setup"
                  color="primary"
                >
                  Link Your Website
                </Button>
              </Access>
            }
          >
            <b>Congratulations!</b> You’ve successfully linked your Connect
            Widget to your church website!
          </Alert>
        )}
        <Box p={2}>
          <DataTable
            loading={loading}
            variant="card"
            size="small"
            data={tableData}
            columns={columns}
            keyField="id"
            onTableChange={onTableChange}
          />
        </Box>
      </>
    </Box>
  );
};

export default ConnectWidgets;
